/* global globalThis */
import React, { useState, useEffect } from "react";
import {
  FaGoogleDrive,
  FaFileExcel,
  FaNetworkWired,
  FaArrowLeft,
} from "react-icons/fa";
import Modal from "../../../common/Modal/Modal";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axiosClient from "../../../../api-client/AxiosClient";
import Spinner from "../../../common/Spinner/Spinner";
import { showError } from "../../../common/Utils";
import "./ImportData.css";

const ImportData = () => {
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedSource, setSelectedSource] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [selectedObject, setSelectedObject] = useState("");
  const [objectDropdownData, setObjectDropdownData] = useState([]);
  const [file, setFile] = useState(null);
  const navigate = useNavigate();
  const [sourceDropdownData, setSourceDropdownData] = useState([]);
  const [templateDropdownData, setTemplateDropdownData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showApiModal, setShowApiModal] = useState(false);
const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
const [showOverrideMergeModal, setShowOverrideMergeModal] = useState(false);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [mismatchedRecords, setMismatchedRecords] = useState([]);
  const [selectedRows, setSelectedRows] = useState({}); // { [mapping_file_details_id]: "Existing" | "Uploaded" }
  const [pendingUploadResponse, setPendingUploadResponse] = useState(null);
  const [existingFiles, setExistingFiles] = useState([]);

  const [apiFormData, setApiFormData] = useState({
    endpoint: "",
    headers: "",
  });

  // ============================================
  // API FIELD CONFIGURATION
  // ============================================
  const apiFieldConfig = {
    api: [
      { name: "endpoint", label: "API Endpoint", type: "text", required: true },
      { name: "headers", label: "Headers", type: "textarea", required: false },
    ],
  };

  // ============================================
  // SOURCE DATA
  // ============================================
  const sourceData = [
    {
      id: "google_sheets",
      name: "Google Sheets",
      icon: <FaGoogleDrive size={40} color="#0F9D58" />,
      action: handleExcelClick,
    },
    {
      id: "excel_csv",
      name: "Excel/CSV",
      icon: <FaFileExcel size={40} color="#217346" />,
      action: handleExcelClick,
    },
    {
      id: "API",
      name: "API",
      icon: <FaNetworkWired size={40} color="#217346" />,
      action: handleApiClick,
    },
  ];

  const filteredSources = sourceData.filter((source) =>
    source.name.toLowerCase().includes(search.toLowerCase()),
  );

  // ============================================
  // EXCEL CLICK HANDLER - FIXED
  // ============================================
  async function handleExcelClick() {
    const organizationConfigId = localStorage.getItem("organization_config_id");

    if (!organizationConfigId) {
      toast.error("Please select a Configuration from the header");
      return;
    }

    setShowModal(true);

    try {
      setLoading(true);

      // Fetch sources and templates sequentially
      await fetchSourcesByConfigID(organizationConfigId);
      await fetchTemplatesByConfigID(organizationConfigId);
    } catch (error) {
      console.error("Error initializing modal:", error);
      toast.error("Failed to load data. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  // ============================================
  // API HANDLERS
  // ============================================
  function handleApiClick() {
    setApiFormData({
      endpoint: "",
      headers: "",
    });
    setShowApiModal(true);
  }

  const handleApiInputChange = (e) => {
    const { name, value } = e.target;
    setApiFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

 // ============================================
// CHECK DUPLICATE FILE
// ============================================
const checkForDuplicateFile = async () => {
  const organizationConfigId = localStorage.getItem("organization_config_id");
  if (!organizationConfigId) return false;

  try {
    const response = await axiosClient.get(
      `/api/Mapper/GetMapperFilesbyOrgConfig/${organizationConfigId}`,
    );
    const files = Array.isArray(response?.data) ? response.data : [];
    setExistingFiles(files);

    console.log("Existing files:", files);
    console.log("Selected object:", selectedObject);

    // API returns 'uploadedObjId' — compare against selectedObject
    const duplicate = files.some(
      (f) => String(f.uploadedObjId || "") === String(selectedObject),
    );

    console.log("Is duplicate:", duplicate);
    return duplicate;
  } catch (err) {
    console.error("checkForDuplicateFile error:", err);
    return false;
  }
};
  const isApiFormValid = () => {
    const fields = apiFieldConfig["api"] || [];
    return fields.every((field) => {
      const value = apiFormData[field.name];
      if (!field.required) return true;
      return value !== undefined && value !== "" && value !== null;
    });
  };

  const handleApiSubmit = async () => {
    const organizationConfigId = localStorage.getItem("organization_config_id");

    if (!organizationConfigId) {
      toast.error("Please select a Configuration from the header");
      return;
    }

    try {
      setLoading(true);
      const response = await axiosClient.post("/api/upload/api", {
        ...apiFormData,
        organizationConfigId: Number(organizationConfigId),
      });

      if (response.status === 200) {
        toast.success("API connection created successfully!");
        setShowApiModal(false);
        setApiFormData({ endpoint: "", headers: "" });
      }
    } catch (err) {
      showError(err);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // FILE CHANGE HANDLER
  // ============================================
  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  // ============================================
  // FORM SUBMIT HANDLER
  // ============================================
// ============================================
// FORM SUBMIT HANDLER
// ============================================
const handleSubmit = async () => {
  const organizationConfigId = localStorage.getItem("organization_config_id");

  if (!organizationConfigId) {
    toast.error("Please select a Configuration from the header");
    return;
  }
  if (!selectedSource) {
    toast.error("Please select a Source");
    return;
  }
  if (!selectedTemplate) {
    toast.error("Please select a Target");
    return;
  }
  if (!selectedObject) {
    toast.error("Please select an Object");
    return;
  }
  if (!file) {
    toast.error("Please select a file to upload");
    return;
  }

  try {
    setLoading(true);
    const isDuplicate = await checkForDuplicateFile();
    setLoading(false); // Always stop loading before branching

    if (isDuplicate) {
      setShowDuplicateWarning(true);
      return; // Stop here — user must confirm in the warning modal
    }

    await proceedWithUpload();
  } catch (error) {
    setLoading(false);
    showError(error);
  }
};
  // ============================================
  // ACTUAL UPLOAD LOGIC (extracted)
  // ============================================
// ============================================
// ACTUAL UPLOAD LOGIC (extracted)
// ============================================
const proceedWithUpload = async (mode = "merge") => {
  const organizationConfigId = localStorage.getItem("organization_config_id");
  setLoading(true);  // ← ensure spinner shows when called from duplicate warning button too
  setShowDuplicateWarning(false); // ← close warning modal if open
  try {
    const formData = new FormData();
    formData.append("File", file);
    formData.append("OrganizationConfigId", organizationConfigId);
    formData.append("TargetId", selectedTemplate);
    formData.append("ObjectId", selectedObject);

    const response = await axiosClient.post("/api/Mapper/Upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    const mistmatched = response.data?.mistmatchedRecords;
    if (mistmatched) {
      const parsed =
        typeof mistmatched === "string"
          ? JSON.parse(mistmatched)
          : mistmatched;

if (parsed && parsed.length > 0 && mode === "merge") {
  setPendingUploadResponse(response.data);
  setMismatchedRecords(parsed);
  setShowModal(false);
  navigate("/upload/mapping-comparison", {
    state: {
      mismatchedRecords: parsed,
      pendingUploadResponse: response.data,
    },
  });
  return;
}

    }

    toast.success("File uploaded successfully!");
    navigate("/view-data", {
      state: { fileID: response.data.userMappingFileId },
    });
  } catch (error) {
    showError(error);
  } finally {
    setLoading(false);
  }
};

  // ============================================
  // APPROVE MISMATCHED RECORDS
  // ============================================
  const handleApproveComparison = async () => {
    if (!pendingUploadResponse) return;

    const userMappingFileId = pendingUploadResponse.userMappingFileId;

    // Build update requests from selected rows
    const updateRequests = Object.entries(selectedRows)
      .map(([detailsId, source]) => {
        const record = mismatchedRecords.find(
          (r) => r.mapping_file_details_id === detailsId && r.Source === source,
        );
        if (!record) return null;

        return {
          UserMappingFileDetailsId: detailsId,
          inscope: record["In-scope"] || "",
          mandatoryfeild: record["Mandatory Field (S4)"] || "",
          mandatoryfield: record["Mandatory Field (S4)"] || "",
          comments: record["Comments"] || "",
          conversionrule: record["Conversion Rules (Logic)"] || "",
          criticalerrorhandling:
            record["Critical Error Handeling (if applicable)"] || "",
          crossreferencetable: record["Cross Reference Table"] || "",
          datacleansingconsiderations:
            record["Data Cleansing Considerations"] || "",
          decimalscale: Number(record["Decimal"]) || 0,
          defaultvalue: record["Default Value"] || "",
          fieldname: record["Field Name (LTMC)"] || "",
          length: record["Length"] || "",
          reusablelogic: record["Reusable Logic (Y/N)"] || "",
          sourcedatalength: record["Source - Data Length"] || "",
          sourcedatatype: record["Source - Data Type"] || "",
          sourcefield: record["Source field (1-to-1 mapping)"] || "",
          sourcefielddescription: record["Source - Field Description"] || "",
          sourcefieldname: record["Source - Field Name"] || "",
          sourcegroupview: record["Source - Group / View"] || "",
          sourcetablename: record["Source - Table Name"] || "",
          sqno: String(record["Sq.No"]) || "",
          targetstructure: record["Target Structure (MC)"] || "",
          transformationrule: record["Transformation Rule"] || "",
          type: record["Type"] || "",
          viewfieldname: record["View - Field Name"] || "",
        };
      })
      .filter(Boolean);

    try {
      setLoading(true);
      await axiosClient.post(
        "https://appservice-falconapi-dev-weu-gjf0egeae5axamau.westeurope-01.azurewebsites.net/api/Mapper/UpdateMapperFile",
        {
          userMappingFileId,
          updateMapperDetailsRequests: updateRequests,
        },
      );

      toast.success("Mapping file updated successfully!");
      setShowComparisonModal(false);
      navigate("/view-data", {
        state: { fileID: userMappingFileId },
      });
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // CANCEL HANDLER - FIXED
  // ============================================
  const handleCancel = () => {
    setShowModal(false);
    setSelectedSource("");
    setSelectedTemplate("");
    setSelectedObject("");
    setFile(null);
    setSourceDropdownData([]);
    setTemplateDropdownData([]);
    setObjectDropdownData([]);
  };

  // ============================================
  // FETCH SOURCES - FIXED
  // ============================================
  const fetchSourcesByConfigID = async (configId) => {
    if (!configId) {
      console.warn("fetchSourcesByConfigID called without configId");
      return;
    }

    try {
      console.log("Fetching sources for configId:", configId);

      const response = await axiosClient.get(
        "/api/MappingDB/CorpMappingDBByUserCorp",
        {
          params: { OrganizationConfigId: configId },
        },
      );

      console.log("Sources response:", response.data);

      if (response.data && Array.isArray(response.data)) {
        setSourceDropdownData(response.data);

        if (response.data.length === 1) {
          const sourceId = String(response.data[0].corpmappingdbid);
          setSelectedSource(sourceId);
          console.log("Auto-selected source:", sourceId);
        } else {
          setSelectedSource("");
        }
      } else {
        setSourceDropdownData([]);
        setSelectedSource("");
      }
    } catch (error) {
      console.error("Error fetching sources:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });

      toast.error(
        error.response?.data?.message ||
          "Error fetching sources. Please try again.",
      );

      setSourceDropdownData([]);
      setSelectedSource("");
    }
  };

  // ============================================
  // FETCH TEMPLATES - FIXED WITH DETAILED LOGGING
  // ============================================
  const fetchTemplatesByConfigID = async (configId) => {
    if (!configId) {
      console.warn("fetchTemplatesByConfigID called without configId");
      toast.error("Organization config ID not found");
      return;
    }

    try {
      console.log("Fetching templates for configId:", configId);
      console.log(
        "API URL:",
        `/api/Target/GetTargetByOrgConfigId?organizationConfigId=${configId}`,
      );

      const response = await axiosClient.get(
        `/api/Target/GetTargetByOrgConfigId?organizationConfigId=${configId}`,
      );

      console.log("Templates response:", response.data);

      if (response.data && Array.isArray(response.data)) {
        setTemplateDropdownData(response.data);

        if (response.data.length === 1) {
          const templateId = String(response.data[0].target_id);
          setSelectedTemplate(templateId);
          console.log("Auto-selected template:", templateId);

          // Auto-fetch objects when template is auto-selected
          await fetchObjectsByTargetId(templateId);
        } else if (response.data.length === 0) {
          console.warn("No templates found for this organization");
          toast.info("No targets found for this organization");
          setSelectedTemplate("");
          setObjectDropdownData([]);
          setSelectedObject("");
        } else {
          setSelectedTemplate("");
          setObjectDropdownData([]);
          setSelectedObject("");
        }
      } else {
        console.warn("Invalid response format:", response.data);
        setTemplateDropdownData([]);
        setSelectedTemplate("");
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
      });

      let errorMessage = "Error fetching targets";

      if (error.response?.status === 404) {
        errorMessage =
          "Target endpoint not found. Please check your API configuration.";
        console.error("404 Error - Possible causes:");
        console.error("1. Backend endpoint doesn't exist");
        console.error("2. Wrong API URL format");
        console.error("3. Organization config ID doesn't exist in database");
      } else if (error.response?.status === 401) {
        errorMessage = "Unauthorized. Please login again.";
      } else if (error.response?.status === 500) {
        errorMessage = "Server error. Please contact support.";
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      toast.error(errorMessage);

      setTemplateDropdownData([]);
      setSelectedTemplate("");
      setObjectDropdownData([]);
      setSelectedObject("");
    }
  };

  // ============================================
  // FETCH OBJECTS - FIXED
  // ============================================
  const fetchObjectsByTargetId = async (targetId) => {
    if (!targetId) {
      console.warn("fetchObjectsByTargetId called without targetId");
      setObjectDropdownData([]);
      setSelectedObject("");
      return;
    }

    try {
      console.log("Fetching objects for targetId:", targetId);

      const response = await axiosClient.get(
        `/api/Object/GetObjectByOrgConfigId`,
        {
          params: {
            OrganizationConfigId: localStorage.getItem(
              "organization_config_id",
            ),
          },
        },
      );

      console.log("Objects response:", response.data);

      if (response.data && Array.isArray(response.data)) {
        setObjectDropdownData(response.data);

        if (response.data.length === 1) {
          const objectId = String(response.data[0].objectid);
          setSelectedObject(objectId);
          console.log("Auto-selected object:", objectId);
        } else if (response.data.length === 0) {
          console.warn("No objects found for this target");
          toast.info("No objects found for this target");
          setSelectedObject("");
        } else {
          setSelectedObject("");
        }
      } else {
        setObjectDropdownData([]);
        setSelectedObject("");
      }
    } catch (error) {
      console.error("Error fetching objects:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });

      toast.error(
        error.response?.data?.message ||
          "Error fetching objects. Please try again.",
      );

      setObjectDropdownData([]);
      setSelectedObject("");
    }
  };

  // ============================================
  // STORAGE CHANGE LISTENER - FIXED
  // ============================================
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.detail.key === "organization_config_id") {
        const newConfigId = e.detail.newValue;

        console.log("Config ID changed:", newConfigId);

        if (showModal) {
          if (newConfigId) {
            // Refetch data with new config ID
            fetchSourcesByConfigID(newConfigId);
            fetchTemplatesByConfigID(newConfigId);
          } else {
            // Clear all data if config is removed
            setSourceDropdownData([]);
            setSelectedSource("");
            setTemplateDropdownData([]);
            setSelectedTemplate("");
            setObjectDropdownData([]);
            setSelectedObject("");
          }
        }
      }
    };

    globalThis.addEventListener("customStorageChange", handleStorageChange);

    return () => {
      globalThis.removeEventListener(
        "customStorageChange",
        handleStorageChange,
      );
    };
  }, [showModal]);

  // ============================================
  // BACK HANDLER
  // ============================================
  const handleBack = () => navigate("/upload");

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="import-container">
      {loading && <Spinner />}

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-gray-900">
          Import Mapping Docs
        </h2>
        <button
          onClick={handleBack}
          className="flex items-center text-blue-600 hover:text-blue-800 p-2 rounded hover:bg-blue-50 transition-colors"
          title="Go back"
        >
          <FaArrowLeft className="mr-2" />
          Back
        </button>
      </div>

      <div className="search-box">
        <input
          type="text"
          placeholder="Search for sources"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="data-sources">
        {filteredSources.map((source) => (
          <button
            key={source.id}
            type="button"
            className="source-card"
            onClick={source.action}
          >
            {source.icon}
            <p>{source.name}</p>
          </button>
        ))}
      </div>

      {/* EXCEL/CSV UPLOAD MODAL */}
      <Modal
        show={showModal}
        setShowModal={setShowModal}
        title="Select Source & Upload"
      >
        <div className="modal-field">
          <label htmlFor="source-name">Source</label>
          <input
            id="source-name"
            type="text"
            className="static-input"
            value={sourceDropdownData?.[0]?.name || "Loading..."}
            disabled
            readOnly
          />
        </div>

        <div className="modal-field">
          <label htmlFor="target-name">Target</label>
          <input
            id="target-name"
            type="text"
            className="static-input"
            value={templateDropdownData?.[0]?.name || "Loading..."}
            disabled
            readOnly
          />
        </div>

        <div className="modal-field">
          <label htmlFor="object-select">Object Name</label>
          <select
            id="object-select"
            className={`static-dropdown${
              objectDropdownData?.length === 1 ? " no-arrow" : ""
            }`}
            value={selectedObject}
            onChange={(e) => setSelectedObject(e.target.value)}
            disabled={objectDropdownData?.length === 1 || loading}
          >
            {objectDropdownData?.length !== 1 && (
              <option value="">Select Object</option>
            )}
            {objectDropdownData?.map((item) => (
              <option key={item.objectid} value={item.objectid}>
                {item.description}
              </option>
            ))}
          </select>
        </div>

        <div className="modal-field">
          <label htmlFor="file-input">File</label>
          <input
            id="file-input"
            type="file"
            accept=".xlsx, .csv"
            onChange={handleFileChange}
            disabled={loading}
          />
        </div>

        <div className="modal-actions">
          <button
            className="cancel-btn"
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="submit-btn"
            onClick={handleSubmit}
            disabled={loading}
          >
            Submit
          </button>
        </div>
      </Modal>

      {/* API CONFIGURATION MODAL */}
      <Modal
        show={showApiModal}
        title="Configure API Source"
        setShowModal={setShowApiModal}
      >
        {apiFieldConfig["api"]?.map((field) => (
          <div key={field.name} className="modal-field">
            <label htmlFor={`api-${field.name}`}>
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {field.type === "textarea" ? (
              <textarea
                id={`api-${field.name}`}
                name={field.name}
                onChange={handleApiInputChange}
                value={apiFormData[field.name] || ""}
                className={field.required ? "required" : ""}
                required={field.required}
                placeholder={
                  field.required
                    ? `Enter ${field.label}`
                    : `Enter ${field.label} (Optional)`
                }
                disabled={loading}
              />
            ) : (
              <input
                id={`api-${field.name}`}
                name={field.name}
                type={field.type}
                value={apiFormData[field.name] || ""}
                onChange={handleApiInputChange}
                className={field.required ? "required" : ""}
                required={field.required}
                placeholder={
                  field.required
                    ? `Enter ${field.label}`
                    : `Enter ${field.label} (Optional)`
                }
                disabled={loading}
              />
            )}
          </div>
        ))}

        <div className="modal-actions">
          <button
            className="cancel-btn"
            onClick={() => setShowApiModal(false)}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className={`submit-btn ${isApiFormValid() ? "" : "disabled"}`}
            onClick={handleApiSubmit}
            disabled={!isApiFormValid() || loading}
          >
            Submit
          </button>
        </div>
      </Modal>
      {/* DUPLICATE FILE WARNING MODAL */}
      {showDuplicateWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-yellow-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h3 className="ml-4 text-lg font-medium text-gray-900">
                Mapping File Already Exists
              </h3>
            </div>
            <p className="text-sm text-gray-500 mb-6">
              A mapping file is already uploaded for this object. Do you want to
              replace it with the new file?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDuplicateWarning(false);
                  setLoading(false);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                No, Cancel
              </button>
<button
  onClick={() => {
    setShowDuplicateWarning(false);
    setShowOverrideMergeModal(true);
  }}
  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
>
  Yes, Continue
</button>

            </div>
          </div>
        </div>
      )}
{/* OVERRIDE OR MERGE MODAL */}
{showOverrideMergeModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
      <div className="flex items-center mb-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
          <svg
            className="w-6 h-6 text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
            />
          </svg>
        </div>
        <h3 className="ml-4 text-lg font-medium text-gray-900">
          How would you like to proceed?
        </h3>
      </div>

<div className="flex gap-2 mb-6">
  {/* Override option */}
  <button
    className="flex-1 flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 hover:border-red-300 hover:bg-red-50 transition-all text-left group"
    onClick={() => {
      setShowOverrideMergeModal(false);
      proceedWithUpload("override");
    }}
  >
    <div className="flex-shrink-0 w-7 h-7 rounded-md bg-red-100 group-hover:bg-red-200 flex items-center justify-center transition-colors">
      <svg className="w-3.5 h-3.5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    </div>
    <div>
      <p className="text-sm font-semibold text-gray-900 leading-none mb-0.5">Override</p>
      <p className="text-xs text-gray-400">Replace existing mapping entirely</p>
    </div>
  </button>

  {/* Merge option */}
  <button
    className="flex-1 flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all text-left group"
    onClick={() => {
      setShowOverrideMergeModal(false);
      proceedWithUpload("merge");
    }}
  >
    <div className="flex-shrink-0 w-7 h-7 rounded-md bg-blue-100 group-hover:bg-blue-200 flex items-center justify-center transition-colors">
      <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 0v10m0-10a2 2 0 012 2h2a2 2 0 012-2V7" />
      </svg>
    </div>
    <div>
      <p className="text-sm font-semibold text-gray-900 leading-none mb-0.5">Merge</p>
      <p className="text-xs text-gray-400">Review & resolve conflicts manually</p>
    </div>
  </button>
</div>

      <div className="flex justify-end">
        <button
          onClick={() => {
            setShowOverrideMergeModal(false);
            setLoading(false);
          }}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
)}

    </div>
  );
};

export default ImportData;
